import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Stepper, Step, StepLabel, Button,
  TextField, Typography, Alert, CircularProgress, Divider,
  Grid, Paper, Chip, LinearProgress, IconButton, InputAdornment, Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Business as OrgIcon,
  Image as ImageIcon,
  RateReview as ReviewIcon,
  Settings as SetupIcon,
  AdminPanelSettings as AdminIcon,
  Visibility, VisibilityOff,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';

const STEPS = [
  { label: 'School Info',     icon: <OrgIcon /> },
  { label: 'Super Admin',     icon: <AdminIcon /> },
  { label: 'Logo & Branding', icon: <ImageIcon /> },
  { label: 'Review & Finish', icon: <ReviewIcon /> },
];

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Box sx={{
        p: 1.2, borderRadius: 2, bgcolor: 'primary.main',
        color: '#fff', display: 'flex', alignItems: 'center',
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
    </Box>
  );
}

export default function SetupWizard() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [step, setStep]       = useState(0);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const [info, setInfo] = useState({
    school_name: '', motto: '', address: '', phone: '', email: '',
    region: '', established_year: '',
  });
  const [admin, setAdmin] = useState({
    first_name: '', last_name: '', email: '', username: '',
    phone: '', password: '', confirm_password: '',
  });
  const [showPw, setShowPw] = useState({ pw: false, confirm: false });
  const [logo, setLogo] = useState('');

  const update = (e) => setInfo({ ...info, [e.target.name]: e.target.value });
  const updateAdmin = (e) => setAdmin({ ...admin, [e.target.name]: e.target.value });

  const handleLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024)     { setError('Image must be smaller than 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogo(reader.result); setError(''); };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (step === 0) {
      if (!info.school_name.trim()) { setError('School name is required'); return false; }
      if (info.email && !/^\S+@\S+\.\S+$/.test(info.email)) { setError('Invalid email address'); return false; }
      if (info.established_year && !/^\d{4}$/.test(info.established_year)) {
        setError('Established year must be 4 digits'); return false;
      }
    }
    if (step === 1) {
      if (!admin.first_name.trim() || !admin.last_name.trim()) { setError('Super admin first and last name are required'); return false; }
      if (!/^\S+@\S+\.\S+$/.test(admin.email)) { setError('Valid super admin email is required'); return false; }
      if (admin.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
      if (admin.password !== admin.confirm_password) { setError('Passwords do not match'); return false; }
    }
    return true;
  };

  const handleNext   = () => { setError(''); if (!validate()) return; setStep((s) => s + 1); };
  const handleBack   = () => { setError(''); setStep((s) => s - 1); };

  const handleFinish = async () => {
    setLoading(true); setError('');
    try {
      const { confirm_password, ...adminPayload } = admin;
      await API.post('/setup/initialize', { ...info, logo, admin: adminPayload });
      window.location.replace('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Setup failed. Please try again.');
      setLoading(false);
    }
  };

  const stepContent = [
    <Box key={0}>
      <SectionHeader icon={<OrgIcon />} title="School Information"
        subtitle="Basic details that will appear across the system" />
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <TextField fullWidth required label="School Name" name="school_name"
            value={info.school_name} onChange={update}
            placeholder="e.g. St. Joseph Senior High School" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="School Motto (optional)" name="motto"
            value={info.motto} onChange={update}
            placeholder="e.g. Knowledge, Discipline, Excellence" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Address" name="address" multiline rows={2}
            value={info.address} onChange={update}
            placeholder="e.g. P.O. Box 123, Kumasi, Ashanti Region" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Phone" name="phone"
            value={info.phone} onChange={update}
            placeholder="e.g. 0244 123 456" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Email" name="email" type="email"
            value={info.email} onChange={update}
            placeholder="e.g. info@school.edu.gh" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Region" name="region"
            value={info.region} onChange={update}
            placeholder="e.g. Ashanti" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Established Year" name="established_year"
            value={info.established_year} onChange={update}
            placeholder="e.g. 1965" inputProps={{ maxLength: 4 }} />
        </Grid>
      </Grid>
    </Box>,

    <Box key={1}>
      <SectionHeader icon={<AdminIcon />} title="Super Administrator"
        subtitle="Create the root account that will manage users, roles and workflows" />
      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
        This account has full access. Use it to create other staff accounts and assign their roles.
      </Alert>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="First Name" name="first_name"
            value={admin.first_name} onChange={updateAdmin}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="Last Name" name="last_name"
            value={admin.last_name} onChange={updateAdmin} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="Email" type="email" name="email"
            value={admin.email} onChange={updateAdmin}
            placeholder="e.g. principal@school.edu.gh" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Username (optional)" name="username"
            value={admin.username} onChange={updateAdmin}
            placeholder="defaults to email if blank" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Phone" name="phone"
            value={admin.phone} onChange={updateAdmin} />
        </Grid>
        <Grid item xs={12} sm={6} />
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="Password" name="password"
            type={showPw.pw ? 'text' : 'password'} value={admin.password} onChange={updateAdmin}
            helperText="Minimum 6 characters"
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={showPw.pw ? 'Hide' : 'Show'}>
                  <IconButton size="small" onClick={() => setShowPw({ ...showPw, pw: !showPw.pw })}>
                    {showPw.pw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            )}} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="Confirm Password" name="confirm_password"
            type={showPw.confirm ? 'text' : 'password'}
            value={admin.confirm_password} onChange={updateAdmin}
            error={!!admin.confirm_password && admin.password !== admin.confirm_password}
            helperText={admin.confirm_password && admin.password !== admin.confirm_password ? 'Passwords do not match' : ' '}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {admin.confirm_password && admin.password === admin.confirm_password && admin.password.length >= 6 && (
                    <CheckIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                  )}
                  <Tooltip title={showPw.confirm ? 'Hide' : 'Show'}>
                    <IconButton size="small" onClick={() => setShowPw({ ...showPw, confirm: !showPw.confirm })}>
                      {showPw.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </InputAdornment>
            )}} />
        </Grid>
      </Grid>
    </Box>,

    <Box key={2}>
      <SectionHeader icon={<ImageIcon />} title="School Logo"
        subtitle="Upload a logo to brand printed reports and dashboards (optional)" />
      <Paper variant="outlined"
        sx={{
          p: 4, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center',
          bgcolor: '#fafafa', cursor: 'pointer',
          '&:hover': { borderColor: 'primary.main', bgcolor: '#f0f4ff' },
        }}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleLogo(e.dataTransfer.files?.[0]); }}
      >
        {logo ? (
          <Box>
            <Box component="img" src={logo} alt="School logo"
              sx={{ maxWidth: 180, maxHeight: 180, mb: 2, borderRadius: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button size="small" variant="outlined" startIcon={<UploadIcon />}
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                Replace
              </Button>
              <Button size="small" color="error" startIcon={<DeleteIcon />}
                onClick={(e) => { e.stopPropagation(); setLogo(''); }}>
                Remove
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" fontWeight={600}>Click or drag an image here</Typography>
            <Typography variant="caption" color="text.secondary">
              PNG or JPG · max 2 MB
            </Typography>
          </Box>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden
          onChange={(e) => handleLogo(e.target.files?.[0])} />
      </Paper>
    </Box>,

    <Box key={3}>
      <SectionHeader icon={<ReviewIcon />} title="Review & Finish"
        subtitle="Confirm your details before initialising the system" />
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {logo && (
            <Box component="img" src={logo} alt=""
              sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5' }} />
          )}
          <Box>
            <Typography variant="h6" fontWeight={800}>{info.school_name || '—'}</Typography>
            {info.motto && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                "{info.motto}"
              </Typography>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          {[
            { label: 'Address',   value: info.address },
            { label: 'Phone',     value: info.phone },
            { label: 'Email',     value: info.email },
            { label: 'Region',    value: info.region },
            { label: 'Established', value: info.established_year },
          ].map((row) => (
            <Grid item xs={12} sm={6} key={row.label}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {row.label}
              </Typography>
              <Typography variant="body2">{row.value || '—'}</Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <AdminIcon color="primary" />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            Super Administrator
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {admin.first_name} {admin.last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {admin.email} {admin.username && `· @${admin.username}`}
        </Typography>
        {admin.phone && (
          <Typography variant="body2" color="text.secondary">{admin.phone}</Typography>
        )}
      </Paper>

      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        After clicking <strong>Finish Setup</strong>, this wizard will be locked. The super
        admin account above will be created and can sign in immediately.
      </Alert>
    </Box>,
  ];

  const progress = (step / (STEPS.length - 1)) * 100;
  const p  = '#1e40af';
  const p2 = '#0ea5e9';

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', p: 2,
      background: `linear-gradient(135deg, ${p} 0%, ${p2} 100%)`,
    }}>
      <Box sx={{ width: '100%', maxWidth: 740 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 60, height: 60, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)', mb: 1.5,
          }}>
            <SetupIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', letterSpacing: -0.5 }}>
            School Setup Wizard
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
            Set up your school's basic information to get started
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{
              height: 5, borderRadius: '12px 12px 0 0', bgcolor: '#e0e7ff',
              '& .MuiLinearProgress-bar': { bgcolor: p, borderRadius: 5 },
            }}
          />

          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stepper activeStep={step} alternativeLabel sx={{ mb: 3.5 }}>
              {STEPS.map((s, i) => (
                <Step key={s.label} completed={i < step}>
                  <StepLabel>
                    <Typography variant="caption" fontWeight={i === step ? 700 : 400}
                      color={i === step ? 'primary' : 'text.secondary'}>
                      {s.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Chip label={`Step ${step + 1} of ${STEPS.length}`} size="small"
                sx={{ bgcolor: '#e0e7ff', color: p, fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {Math.round(progress)}% complete
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box sx={{ minHeight: 280 }}>{stepContent[step]}</Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button onClick={handleBack} disabled={step === 0} variant="outlined" sx={{ minWidth: 100 }}>
                Back
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                {STEPS.map((_, i) => (
                  <Box key={i} sx={{
                    width: i === step ? 22 : 8, height: 8, borderRadius: 4,
                    bgcolor: i === step ? p : i < step ? p2 : '#e0e0e0',
                    transition: 'all 0.3s ease',
                  }} />
                ))}
              </Box>

              {step < STEPS.length - 1 ? (
                <Button onClick={handleNext} variant="contained" sx={{ minWidth: 100 }}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleFinish} variant="contained" disabled={loading} sx={{ minWidth: 150 }}>
                  {loading
                    ? <><CircularProgress size={15} color="inherit" sx={{ mr: 1 }} /> Setting up…</>
                    : 'Finish Setup'
                  }
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2.5, color: 'rgba(255,255,255,0.6)' }}>
          You can update these details later from the system settings.
        </Typography>
      </Box>
    </Box>
  );
}
